import { useState, type ReactNode } from 'react';
import { TriangleAlert } from 'lucide-react';
import { Alert, Button, Modal } from './ui';
import { ApiError } from '../lib/api';

/**
 * Confirmação para ação que não tem volta.
 *
 * Existe porque dava para apagar a Licença de Operação de um fornecedor com um
 * clique na lixeira — sem pergunta, sem desfazer, e sem nem saber qual das duas
 * linhas era. Ícone pequeno em lista densa é onde o clique erra.
 *
 * O texto é obrigatório e específico de propósito: "Tem certeza?" não ajuda
 * ninguém a decidir. Quem confirma precisa ler **o que** vai sumir e **o que
 * acontece depois** — se dá para refazer, se algo mais vai junto.
 */
export function useConfirm() {
  const [pedido, setPedido] = useState<ConfirmRequest | null>(null);
  const [executando, setExecutando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function confirmar() {
    if (!pedido) return;
    setExecutando(true);
    setErro(null);
    try {
      await pedido.onConfirm();
      setPedido(null);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Não foi possível concluir a ação.');
    } finally {
      setExecutando(false);
    }
  }

  return {
    /** Abre a confirmação. A ação só roda depois do "sim". */
    ask: (request: ConfirmRequest) => {
      setErro(null);
      setPedido(request);
    },
    dialog: (
      <Modal
        open={pedido !== null}
        onClose={() => (executando ? undefined : setPedido(null))}
        title={pedido?.title ?? ''}
      >
        <div className="space-y-4">
          {erro && <Alert>{erro}</Alert>}

          <div className="flex gap-3">
            <TriangleAlert className="mt-0.5 size-5 shrink-0 text-amber-600" aria-hidden />
            <div className="space-y-2 text-sm text-slate-700">{pedido?.body}</div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={executando} onClick={() => setPedido(null)}>
              Cancelar
            </Button>
            <Button
              variant={pedido?.destructive === false ? 'primary' : 'danger'}
              loading={executando}
              onClick={() => void confirmar()}
            >
              {pedido?.confirmLabel ?? 'Confirmar'}
            </Button>
          </div>
        </div>
      </Modal>
    ),
  };
}

export interface ConfirmRequest {
  title: string;
  /** O que vai acontecer, em português e específico. */
  body: ReactNode;
  /** O rótulo diz o verbo, não "OK": "Apagar a LO", "Despublicar". */
  confirmLabel?: string;
  /** `false` para ação importante mas não destrutiva — o botão deixa de ser vermelho. */
  destructive?: boolean;
  onConfirm: () => Promise<void>;
}
